import { useState } from 'react';
import { modelDropDownMask } from '@/utilities/dropdownHelpers';

type SelectOption = { value: string; label: string };
type OptionGroup = { optgroup: string; options: SelectOption[] };
type ModelItem = {
  value?: string;
  label?: string;
  modelCode?: string;
  modelDescription?: string;
};

const DEFAULT_MODEL_OPTIONS: (SelectOption | OptionGroup)[] = [{ value: '', label: 'Any Model' }];

/**
 * Custom hook for managing model dropdown selection
 * Handles model selection changes and displays available models
 */
export const useModelDropdown = () => {
  const [selectedModel, setSelectedModel] = useState<string>('');
  const [modelOptions, setModelOptions] = useState<(SelectOption | OptionGroup)[]>(DEFAULT_MODEL_OPTIONS);

  /**
   * Handle model selection change and fire analytics event
   */
  const handleModelChange = (
    event: React.ChangeEvent<HTMLSelectElement>,
  ) => {
    const selectedModelCode = event.target.value;
    const selectedModelLabel = event.target.options[event.target.selectedIndex]?.text || '';

    setSelectedModel(selectedModelCode);

    // Component can handle analytics events separately via onChange callback
  };

  /**
   * Update model options based on fetched models.
   * Async because modelDropDownMask lazy-loads the popular-models data
   * (~31KB) on first invocation to keep it out of the initial bundle.
   */
  const updateModelOptions = async (
    models: ModelItem[],
    makeCode: string = '',
  ): Promise<void> => {
    const maskedOptions = await modelDropDownMask(models, makeCode);
    setModelOptions(maskedOptions);
  };

  return {
    selectedModel,
    setSelectedModel,
    modelOptions,
    setModelOptions,
    handleModelChange,
    updateModelOptions,
  };
};
